/**
 * "Input data" flyout on the left edge of the node-config panel: lists the
 * known output fields per upstream node; clicking inserts the ``{{ … }}``
 * expression at the caret (or copies it without an active field). Field
 * names only, never values — no tenant data is rendered or persisted.
 */

import { FC, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RemoveIcon from '@mui/icons-material/Remove'
import { themeColor } from '../../../layout/theme/color'
import { useActiveExpressionField, useAutocompleteContext } from '../autocomplete/ExpressionContext'
import { MONO_FONT } from '../config/jsonEditing'
import { nodeAppear } from '../nodes/BaseNode'
import FieldChips, { FieldPickResult } from './FieldChips'

const ACCENT = themeColor.blue
const TAB_WIDTH = 30
const GLASS = 'rgba(255,255,255,0.92)'
const EDGE_BORDER = '1px solid rgba(0,0,0,0.06)'

interface InputDataFlyoutProps {
  open: boolean
  onToggle: () => void
}

const InputDataFlyout: FC<InputDataFlyoutProps> = ({ open, onToggle }) => {
  const translate = useTranslate()
  const ctx = useAutocompleteContext()
  const registry = useActiveExpressionField()
  // Kept here so it survives minimising the flyout; session-only.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})

  const toggleSection = (id: string) =>
    // eslint-disable-next-line security/detect-object-injection
    setCollapsedSections((prev) => ({ ...prev, [id]: !prev[id] }))

  const meta = ctx.nodeMeta ?? {}
  const sourceLabelKey = (nodeType?: string): string => {
    if (nodeType === 'trigger') return 'workflow.config.inputDataFromTrigger'
    if (nodeType === 'ai_call') return 'workflow.config.inputDataFromAi'
    return 'workflow.config.inputDataFromTest'
  }
  const sections = ctx.availableNodeIds
    .map((id) => ({
      id,
      label: meta[id]?.label ?? id, // eslint-disable-line security/detect-object-injection
      isTrigger: meta[id]?.type === 'trigger', // eslint-disable-line security/detect-object-injection
      sourceKey: sourceLabelKey(meta[id]?.type), // eslint-disable-line security/detect-object-injection
      fields: ctx.nodeOutputKeys[id] ?? [], // eslint-disable-line security/detect-object-injection
    }))
    .filter((s) => s.fields.length > 0)

  const fieldCount = sections.reduce((sum, s) => sum + s.fields.length, 0)

  const handlePick = (_field: string, expression: string): FieldPickResult => {
    if (registry.insertIntoActiveField(expression)) return 'inserted'
    void navigator.clipboard.writeText(expression)
    return 'copied'
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 52,
        // Keeps the React Flow minimap (bottom-right) reachable; long lists scroll internally.
        bottom: 170,
        right: '100%',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          maxHeight: 'min(600px, 100%)',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          background: GLASS,
          backdropFilter: 'blur(12px)',
          border: EDGE_BORDER,
          borderRight: 'none',
          borderRadius: '12px 0 0 12px',
          boxShadow: open
            ? '-12px 0 40px rgba(0,0,0,0.10), -2px 0 8px rgba(0,0,0,0.04)'
            : '-2px 0 8px rgba(0,0,0,0.04), -1px 0 2px rgba(0,0,0,0.06)',
          transition: 'box-shadow 0.25s cubic-bezier(0.4,0,0.2,1)',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={onToggle}
          sx={{
            'position': 'relative',
            'width': TAB_WIDTH,
            'minHeight': 96,
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'justifyContent': 'center',
            'gap': 0.75,
            'py': 1.5,
            'cursor': 'pointer',
            'userSelect': 'none',
            'color': open ? ACCENT : themeColor.slate40,
            'transition': 'background 0.2s ease, color 0.2s ease',
            '&:hover': {
              background: 'rgba(255,255,255,0.98)',
              color: open ? ACCENT : themeColor.slate60,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              borderRadius: '12px 0 0 12px',
              background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT}88)`,
              opacity: open ? 1 : 0,
              transition: 'opacity 0.2s ease',
            },
          }}
        >
          <Typography
            component="span"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {translate('workflow.config.inputData')}
          </Typography>
          {fieldCount > 0 && (
            <Typography component="span" sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>
              {fieldCount}
            </Typography>
          )}
        </Box>

        <Collapse
          orientation="horizontal"
          in={open}
          timeout={250}
          unmountOnExit
          sx={{ '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { height: '100%' } }}
        >
          <Box
            sx={{
              width: 340,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderLeft: '1px solid rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: EDGE_BORDER,
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: themeColor.slate60, lineHeight: 1.6 }}
              >
                {translate('workflow.config.inputData')}
              </Typography>
              {fieldCount > 0 && (
                <Typography
                  component="span"
                  sx={{ fontSize: 10, fontWeight: 700, color: ACCENT, lineHeight: 1 }}
                >
                  {fieldCount}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={onToggle}>
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
              {sections.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{ color: themeColor.slate50, display: 'block', lineHeight: 1.4 }}
                >
                  {translate('workflow.config.inputDataEmpty')}
                </Typography>
              ) : (
                <>
                  <Typography
                    variant="caption"
                    sx={{ color: themeColor.slate50, display: 'block', lineHeight: 1.4, mb: 1.25 }}
                  >
                    {translate('workflow.config.inputDataHint')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {sections.map((section, index) => {
                      const isCollapsed = !!collapsedSections[section.id] // eslint-disable-line security/detect-object-injection
                      return (
                        <Box
                          key={section.id}
                          sx={{
                            animation: `${nodeAppear} 0.35s cubic-bezier(0.4,0,0.2,1) both`,
                            animationDelay: `${Math.min(index * 40, 160)}ms`,
                          }}
                        >
                          <Box
                            onClick={() => toggleSection(section.id)}
                            sx={{
                              'display': 'flex',
                              'alignItems': 'baseline',
                              'gap': 0.75,
                              'mb': 0.5,
                              'px': 0.75,
                              'py': 0.25,
                              'mx': -0.75,
                              'cursor': 'pointer',
                              'borderRadius': '8px',
                              'userSelect': 'none',
                              '&:hover': { background: themeColor.slate05 },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: themeColor.slate60, lineHeight: 1.2 }}
                            >
                              {section.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: themeColor.slate40,
                                fontSize: 9.5,
                                fontFamily: MONO_FONT,
                                lineHeight: 1.2,
                              }}
                            >
                              {translate(section.sourceKey)}
                              {isCollapsed ? ` · ${section.fields.length}` : ''}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {isCollapsed ? (
                              <ExpandMoreIcon sx={{ fontSize: 14, color: themeColor.slate40, alignSelf: 'center' }} />
                            ) : (
                              <ExpandLessIcon sx={{ fontSize: 14, color: themeColor.slate40, alignSelf: 'center' }} />
                            )}
                          </Box>
                          <Collapse in={!isCollapsed}>
                            <FieldChips
                              fields={section.fields}
                              expressionFor={(field) =>
                                section.isTrigger
                                  ? `{{ trigger.${field} }}`
                                  : `{{ nodes.${section.id}.output.${field} }}`
                              }
                              onPick={handlePick}
                              pickedTooltip={(result) =>
                                translate(
                                  result === 'inserted'
                                    ? 'workflow.config.inputDataInserted'
                                    : 'workflow.config.inputDataCopied',
                                )
                              }
                              action="insert"
                            />
                          </Collapse>
                        </Box>
                      )
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}

export default InputDataFlyout
